/**
 * Whole-object operations that act on a set of references: move, duplicate, and clipboard.
 *
 * Translation clamps the group as a unit rather than clamping each vertex, because per-vertex
 * clamping would deform a shape the moment any part of it reached the map edge.
 */

import { cloneRgba } from '../color.ts';
import { clonePoint } from './document.ts';
import { cloneRing } from './ring.ts';
import { cloneIslandLook } from './surfaces.ts';
import { cloneRoute } from './routes.ts';
import { refBounds, selectionBounds, type ObjectRef, type Rect } from './selection.ts';
import type {
  Checkpoint,
  Island,
  MapBounds,
  MapDocument,
  MapRoute,
  Rock,
  ShallowWaterZone,
  StartArea,
  Vec2,
} from './types.ts';

export type ScaleRefsOptions = {
  /** One factor on both axes, clamped so X and Y stay equal and on the map. */
  uniform?: boolean;
};

export type MapClipboard = {
  islands: Island[];
  rocks: Rock[];
  shallowWater: ShallowWaterZone[];
  checkpoints: Checkpoint[];
  routes: MapRoute[];
  /** Top-left of the copied group, so a paste can be positioned relative to it. */
  origin: Vec2;
};

function movePoints(points: Vec2[], delta: Vec2): void {
  for (const point of points) {
    point.x += delta.x;
    point.y += delta.y;
  }
}

function moveRing(ring: Island | Rock | ShallowWaterZone | StartArea, delta: Vec2): void {
  movePoints(ring.vertices, delta);

  for (const edge of ring.edges) {
    if (edge.bend) {
      movePoints([edge.bend], delta);
    }
  }

  if ('decorations' in ring) {
    for (const decoration of ring.decorations) {
      decoration.x += delta.x;
      decoration.y += delta.y;
    }
  }
}

/** Shrinks `delta` so the whole group stays inside the map, preserving relative positions. */
export function clampGroupDelta(
  map: MapDocument,
  refs: readonly ObjectRef[],
  delta: Vec2,
): Vec2 {
  const bounds = selectionBounds(map, refs);

  if (!bounds) {
    return { x: 0, y: 0 };
  }

  return {
    x: Math.max(-bounds.minX, Math.min(map.bounds.width - bounds.maxX, delta.x)),
    y: Math.max(-bounds.minY, Math.min(map.bounds.height - bounds.maxY, delta.y)),
  };
}

/** Moves every referenced object by the same clamped delta. Returns the delta actually applied. */
export function translateRefs(map: MapDocument, refs: readonly ObjectRef[], delta: Vec2): Vec2 {
  const applied = clampGroupDelta(map, refs, delta);

  if (applied.x === 0 && applied.y === 0) {
    return applied;
  }

  for (const ref of refs) {
    switch (ref.kind) {
      case 'island': {
        const island = map.islands.find((item) => item.id === ref.id);

        if (island) {
          moveRing(island, applied);
        }

        break;
      }
      case 'rock': {
        const rock = map.rocks.find((item) => item.id === ref.id);

        if (rock) {
          moveRing(rock, applied);
        }

        break;
      }
      case 'water': {
        const zone = map.shallowWater.find((item) => item.id === ref.id);

        if (zone) {
          moveRing(zone, applied);
        }

        break;
      }
      case 'startArea': {
        if (map.startArea) {
          moveRing(map.startArea, applied);
        }

        break;
      }
      case 'parkArea': {
        if (map.parkArea) {
          moveRing(map.parkArea, applied);
        }

        break;
      }
      case 'checkpoint': {
        const checkpoint = map.checkpoints.find((item) => item.id === ref.id);

        if (checkpoint) {
          movePoints([checkpoint.a, checkpoint.b], applied);
        }

        break;
      }
      case 'route': {
        const route = map.routes.find((item) => item.id === ref.id);

        if (route) {
          movePoints(route.points, applied);
        }

        break;
      }
    }
  }

  return applied;
}

const MIN_GROUP_SCALE = 0.05;

function scalePoint(point: Vec2, origin: Vec2, scaleX: number, scaleY: number): void {
  point.x = origin.x + (point.x - origin.x) * scaleX;
  point.y = origin.y + (point.y - origin.y) * scaleY;
}

function limitScale1d(
  origin: number,
  values: readonly number[],
  scale: number,
  min: number,
  max: number,
): number {
  let next = Number.isFinite(scale) ? scale : 1;

  if (next < MIN_GROUP_SCALE) {
    next = MIN_GROUP_SCALE;
  }

  for (const value of values) {
    const delta = value - origin;

    if (delta === 0) {
      continue;
    }

    const toMin = (min - origin) / delta;
    const toMax = (max - origin) / delta;

    if (delta > 0) {
      next = Math.min(next, toMax);
      next = Math.max(next, toMin);
    } else {
      next = Math.min(next, toMin);
      next = Math.max(next, toMax);
    }
  }

  return Number.isFinite(next) ? next : 1;
}

function boundValues(bounds: Rect, axis: 'x' | 'y'): number[] {
  return axis === 'x' ? [bounds.minX, bounds.maxX] : [bounds.minY, bounds.maxY];
}

function scaleRing(ring: Island | Rock | ShallowWaterZone | StartArea, origin: Vec2, scaleX: number, scaleY: number): void {
  for (const vertex of ring.vertices) {
    scalePoint(vertex, origin, scaleX, scaleY);
  }

  for (const edge of ring.edges) {
    if (edge.bend) {
      scalePoint(edge.bend, origin, scaleX, scaleY);
    }
  }

  if ('decorations' in ring) {
    const propScale = Math.sqrt(Math.abs(scaleX * scaleY));

    for (const decoration of ring.decorations) {
      scalePoint(decoration, origin, scaleX, scaleY);
      decoration.scale *= propScale;
    }
  }
}

/**
 * Scales every referenced object about `origin`. Factors are clamped so the group stays on the
 * map and does not flip. Returns the factors that were actually applied.
 */
export function scaleRefs(
  map: MapDocument,
  refs: readonly ObjectRef[],
  origin: Vec2,
  scaleX: number,
  scaleY: number,
  options?: ScaleRefsOptions,
): { scaleX: number; scaleY: number } {
  const bounds = selectionBounds(map, refs);

  if (!bounds || refs.length === 0) {
    return { scaleX: 1, scaleY: 1 };
  }

  const limitedX = limitScale1d(origin.x, boundValues(bounds, 'x'), scaleX, 0, map.bounds.width);
  const limitedY = limitScale1d(origin.y, boundValues(bounds, 'y'), scaleY, 0, map.bounds.height);
  const uniform = options?.uniform === true ? Math.min(limitedX, limitedY) : null;
  const applied = {
    scaleX: uniform ?? limitedX,
    scaleY: uniform ?? limitedY,
  };

  if (applied.scaleX === 1 && applied.scaleY === 1) {
    return applied;
  }

  for (const ref of refs) {
    switch (ref.kind) {
      case 'island': {
        const island = map.islands.find((item) => item.id === ref.id);
        if (island) scaleRing(island, origin, applied.scaleX, applied.scaleY);
        break;
      }
      case 'rock': {
        const rock = map.rocks.find((item) => item.id === ref.id);
        if (rock) scaleRing(rock, origin, applied.scaleX, applied.scaleY);
        break;
      }
      case 'water': {
        const zone = map.shallowWater.find((item) => item.id === ref.id);
        if (zone) scaleRing(zone, origin, applied.scaleX, applied.scaleY);
        break;
      }
      case 'startArea': {
        if (map.startArea) {
          scaleRing(map.startArea, origin, applied.scaleX, applied.scaleY);
        }
        break;
      }
      case 'parkArea': {
        if (map.parkArea) {
          scaleRing(map.parkArea, origin, applied.scaleX, applied.scaleY);
        }
        break;
      }
      case 'checkpoint': {
        const checkpoint = map.checkpoints.find((item) => item.id === ref.id);

        if (checkpoint) {
          scalePoint(checkpoint.a, origin, applied.scaleX, applied.scaleY);
          scalePoint(checkpoint.b, origin, applied.scaleX, applied.scaleY);
        }

        break;
      }
      case 'route': {
        const route = map.routes.find((item) => item.id === ref.id);

        if (route) {
          for (const point of route.points) {
            scalePoint(point, origin, applied.scaleX, applied.scaleY);
          }
        }

        break;
      }
    }
  }

  return applied;
}

function pointInMap(point: Vec2, bounds: MapBounds): boolean {
  return point.x >= 0 && point.y >= 0 && point.x <= bounds.width && point.y <= bounds.height;
}

function rotatedPoint(point: Vec2, origin: Vec2, radians: number): Vec2 {
  const sin = Math.sin(radians);
  const cos = Math.cos(radians);
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  return {
    x: origin.x + dx * cos - dy * sin,
    y: origin.y + dx * sin + dy * cos,
  };
}

function forEachRefPoint(
  map: MapDocument,
  refs: readonly ObjectRef[],
  visit: (point: Vec2) => void,
): void {
  for (const ref of refs) {
    switch (ref.kind) {
      case 'island': {
        const island = map.islands.find((item) => item.id === ref.id);
        if (island) {
          island.vertices.forEach(visit);
          for (const edge of island.edges) {
            if (edge.bend) {
              visit(edge.bend);
            }
          }
          for (const decoration of island.decorations) {
            visit(decoration);
          }
        }
        break;
      }
      case 'rock': {
        const rock = map.rocks.find((item) => item.id === ref.id);
        if (rock) {
          rock.vertices.forEach(visit);
          for (const edge of rock.edges) {
            if (edge.bend) {
              visit(edge.bend);
            }
          }
        }
        break;
      }
      case 'water': {
        const zone = map.shallowWater.find((item) => item.id === ref.id);
        if (zone) {
          zone.vertices.forEach(visit);
          for (const edge of zone.edges) {
            if (edge.bend) {
              visit(edge.bend);
            }
          }
        }
        break;
      }
      case 'startArea': {
        if (map.startArea) {
          map.startArea.vertices.forEach(visit);
          for (const edge of map.startArea.edges) {
            if (edge.bend) {
              visit(edge.bend);
            }
          }
        }
        break;
      }
      case 'parkArea': {
        if (map.parkArea) {
          map.parkArea.vertices.forEach(visit);
          for (const edge of map.parkArea.edges) {
            if (edge.bend) {
              visit(edge.bend);
            }
          }
        }
        break;
      }
      case 'checkpoint': {
        const checkpoint = map.checkpoints.find((item) => item.id === ref.id);
        if (checkpoint) {
          visit(checkpoint.a);
          visit(checkpoint.b);
        }
        break;
      }
      case 'route': {
        const route = map.routes.find((item) => item.id === ref.id);
        if (route) {
          route.points.forEach(visit);
        }
        break;
      }
    }
  }
}

/**
 * Rotates every referenced object about `origin`. Returns `radians` when every point stays on
 * the map, otherwise 0 and the document is unchanged.
 */
export function rotateRefs(
  map: MapDocument,
  refs: readonly ObjectRef[],
  origin: Vec2,
  radians: number,
): number {
  if (refs.length === 0 || !Number.isFinite(radians) || radians === 0) {
    return 0;
  }

  const preview: Vec2[] = [];
  forEachRefPoint(map, refs, (point) => {
    preview.push(rotatedPoint(point, origin, radians));
  });

  if (preview.length === 0 || preview.some((point) => !pointInMap(point, map.bounds))) {
    return 0;
  }

  forEachRefPoint(map, refs, (point) => {
    const next = rotatedPoint(point, origin, radians);
    point.x = next.x;
    point.y = next.y;
  });

  for (const ref of refs) {
    if (ref.kind !== 'island') {
      continue;
    }

    const island = map.islands.find((item) => item.id === ref.id);

    if (!island) {
      continue;
    }

    for (const decoration of island.decorations) {
      decoration.rotation += radians;
    }
  }

  return radians;
}

function cloneCheckpoint(checkpoint: Checkpoint): Checkpoint {
  return {
    ...checkpoint,
    a: clonePoint(checkpoint.a),
    b: clonePoint(checkpoint.b),
  };
}

/**
 * Copies the referenced objects out of the map. The start area is skipped: a map has at most one,
 * so pasting a second would silently replace the first.
 */
export function copyRefs(map: MapDocument, refs: readonly ObjectRef[]): MapClipboard {
  const clipboard: MapClipboard = {
    islands: [],
    rocks: [],
    shallowWater: [],
    checkpoints: [],
    routes: [],
    origin: { x: 0, y: 0 },
  };

  for (const ref of refs) {
    switch (ref.kind) {
      case 'island': {
        const island = map.islands.find((item) => item.id === ref.id);

        if (island) {
          clipboard.islands.push({
            ...cloneRing(island),
            id: island.id,
            color: cloneRgba(island.color),
            elevation: island.elevation,
            ...cloneIslandLook(island),
          });
        }

        break;
      }
      case 'rock': {
        const rock = map.rocks.find((item) => item.id === ref.id);

        if (rock) {
          clipboard.rocks.push({
            ...cloneRing(rock),
            id: rock.id,
            color: cloneRgba(rock.color),
            elevation: rock.elevation,
          });
        }

        break;
      }
      case 'water': {
        const zone = map.shallowWater.find((item) => item.id === ref.id);

        if (zone) {
          clipboard.shallowWater.push({
            ...cloneRing(zone),
            id: zone.id,
            material: zone.material,
            color: cloneRgba(zone.color),
            depth: zone.depth,
          });
        }

        break;
      }
      case 'checkpoint': {
        const checkpoint = map.checkpoints.find((item) => item.id === ref.id);

        if (checkpoint) {
          clipboard.checkpoints.push(cloneCheckpoint(checkpoint));
        }

        break;
      }
      case 'route': {
        const route = map.routes.find((item) => item.id === ref.id);

        if (route) {
          clipboard.routes.push(cloneRoute(route));
        }

        break;
      }
      case 'startArea':
      case 'parkArea':
        break;
    }
  }

  const bounds = selectionBounds(
    map,
    refs.filter((ref) => ref.kind !== 'startArea' && ref.kind !== 'parkArea'),
  );

  if (bounds) {
    clipboard.origin = { x: bounds.minX, y: bounds.minY };
  }

  return clipboard;
}

export function isClipboardEmpty(clipboard: MapClipboard | null): boolean {
  if (!clipboard) {
    return true;
  }

  return (
    clipboard.islands.length === 0 &&
    clipboard.rocks.length === 0 &&
    clipboard.shallowWater.length === 0 &&
    clipboard.checkpoints.length === 0 &&
    clipboard.routes.length === 0
  );
}

/**
 * Inserts clipboard contents into the map, offset by `delta`, with freshly allocated ids.
 * Returns references to the new objects so the caller can select them.
 */
export function pasteClipboard(
  map: MapDocument,
  clipboard: MapClipboard,
  delta: Vec2,
): ObjectRef[] {
  const created: ObjectRef[] = [];

  for (const source of clipboard.islands) {
    const island: Island = {
      ...cloneRing(source),
      id: `island-${map.nextIslandId}`,
      color: cloneRgba(source.color),
      elevation: source.elevation,
      ...cloneIslandLook(source),
    };
    map.nextIslandId += 1;
    moveRing(island, delta);
    map.islands.push(island);
    created.push({ kind: 'island', id: island.id });
  }

  for (const source of clipboard.rocks) {
    const rock: Rock = {
      ...cloneRing(source),
      id: `rock-${map.nextRockId}`,
      color: cloneRgba(source.color),
      elevation: source.elevation,
    };
    map.nextRockId += 1;
    moveRing(rock, delta);
    map.rocks.push(rock);
    created.push({ kind: 'rock', id: rock.id });
  }

  for (const source of clipboard.shallowWater) {
    const zone: ShallowWaterZone = {
      ...cloneRing(source),
      id: `water-${map.nextShallowWaterId}`,
      material: source.material,
      color: cloneRgba(source.color),
      depth: source.depth,
    };
    map.nextShallowWaterId += 1;
    moveRing(zone, delta);
    map.shallowWater.push(zone);
    created.push({ kind: 'water', id: zone.id });
  }

  for (const source of clipboard.checkpoints) {
    const checkpoint = cloneCheckpoint(source);
    checkpoint.id = `checkpoint-${map.nextCheckpointId}`;
    map.nextCheckpointId += 1;
    movePoints([checkpoint.a, checkpoint.b], delta);
    map.checkpoints.push(checkpoint);
    created.push({ kind: 'checkpoint', id: checkpoint.id });
  }

  for (const source of clipboard.routes) {
    const route = cloneRoute(source);
    route.id = `route-${map.nextRouteId}`;
    map.nextRouteId += 1;
    movePoints(route.points, delta);
    map.routes.push(route);
    created.push({ kind: 'route', id: route.id });
  }

  // Nudge the group back inside as a unit, so a paste near the edge slides into view.
  translateRefs(map, created, clampGroupDelta(map, created, { x: 0, y: 0 }));

  return created;
}

/** Copy-and-paste in one step, offset by `delta`. */
export function duplicateRefs(
  map: MapDocument,
  refs: readonly ObjectRef[],
  delta: Vec2,
): ObjectRef[] {
  return pasteClipboard(map, copyRefs(map, refs), delta);
}

export function refCenter(map: MapDocument, ref: ObjectRef): Vec2 | null {
  const rect = refBounds(map, ref);

  if (!rect) {
    return null;
  }

  return { x: (rect.minX + rect.maxX) / 2, y: (rect.minY + rect.maxY) / 2 };
}
